import React, { useMemo, useRef, useState } from "react";
import {
  IoCalendarOutline,
  IoTimeOutline,
  IoChevronDown,
  IoCloseOutline,
} from "react-icons/io5";
import { FiPlus, FiSave, FiTrash2 } from "react-icons/fi";

import { INCIDENT_TYPES } from "../components/incidentTypes";

function DynamicField({
  field,
  value,
  onChange,
  focusClass,
}) {
  const commonClass = `
    w-full text-sm
    border border-slate-200
    rounded-xl
    px-3 py-2.5
    focus:outline-none
    ${focusClass}
  `;

  return (
    <div className="flex flex-col gap-1.5">

      <label className="text-xs font-bold text-slate-700">
        {field.label}

        {field.required && (
          <span className="text-red-500"> *</span>
        )}
      </label>

      {field.type === 'select' && (
        <div className="relative">

          <select
            name={field.name}
            value={value}
            onChange={onChange}
            required={field.required}
            className={`${commonClass} appearance-none bg-white text-slate-600`}
          >
            <option value="">
              Select {field.label.toLowerCase()}
            </option>

            {field.options?.map((option) => (
              <option
                key={option}
                value={option}
              >
                {option}
              </option>
            ))}
          </select>

          <IoChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />

        </div>
      )}

      {field.type === 'text' && (
        <input
          type="text"
          name={field.name}
          value={value}
          onChange={onChange}
          required={field.required}
          placeholder={field.placeholder}
          className={commonClass}
        />
      )}

      {field.type === 'textarea' && (
        <textarea
          name={field.name}
          rows="3"
          value={value}
          onChange={onChange}
          required={field.required}
          placeholder={field.placeholder}
          className={`${commonClass} resize-none leading-relaxed`}
        />
      )}

    </div>
  );
}

export default function CreateIncidentModal({
  incidentType,
  onClose,
  onSubmit,
}) {
  const fileInputRef = useRef(null);

  const config = useMemo(
    () => INCIDENT_TYPES.find((item) => item.value === incidentType),
    [incidentType],
  );

  const [formData, setFormData] = useState({
    location: "",
    date: new Date().toISOString().split("T")[0],
    time: new Date().toTimeString().slice(0, 5),
  });

  const [images, setImages] = useState([]);

  if (!config) {
    return null;
  }

  const IconComponent = config.icon;

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFiles = (e) => {
    const files = Array.from(e.target.files || []);

    setImages((prev) => [...prev, ...files]);

    // Allows selecting the same file again later
    e.target.value = "";
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    onSubmit?.({
      ...formData,
      incidentType: config.value,
      photos: images,
    });
  };

  return (
    <div className="w-full bg-white font-sans rounded-xl text-slate-800">
      {/* Header */}
      <div className="p-5 pb-4 border-b border-slate-100 flex justify-between items-start">
        <div className="flex items-center gap-3">
          <div className={`p-2.5 rounded-xl border ${config.colors.icon}`}>
            <IconComponent className="text-xl" />
          </div>

          <div>
            <h2 className="text-lg font-bold tracking-tight">
              Create {config.title}
            </h2>

            <p className="text-xs text-slate-400 mt-0.5">
              {config.description}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="text-slate-400 hover:text-slate-600 transition-colors p-1"
        >
          <IoCloseOutline className="text-2xl" />
        </button>
      </div>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className="p-5 space-y-4 max-h-[75vh] overflow-y-auto"
      >
        {/* Location + Date + Time */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Location */}
          {config.fields.some((field) => field.name === "location") && (
            <DynamicField
              field={config.fields.find((field) => field.name === "location")}
              value={formData.location}
              onChange={handleChange}
              focusClass={config.colors.focus}
            />
          )}

          {/* Date & Time */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold text-slate-700">
              Date & Time <span className="text-red-500">*</span>
            </label>

            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                  className={`w-full text-sm border border-slate-200 rounded-xl pl-3 pr-8 py-2.5 focus:outline-none ${config.colors.focus}`}
                />

                <IoCalendarOutline className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-base" />
              </div>

              <div className="relative w-28">
                <input
                  type="time"
                  name="time"
                  value={formData.time}
                  onChange={handleChange}
                  required
                  className={`w-full text-sm border border-slate-200 rounded-xl pl-3 pr-7 py-2.5 focus:outline-none ${config.colors.focus}`}
                />

                <IoTimeOutline className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none text-base" />
              </div>
            </div>
          </div>
        </div>

        {/* Dynamic Fields */}
        {config.fields
          .filter((field) => field.name !== "location")
          .map((field) => (
            <DynamicField
              key={field.name}
              field={field}
              value={formData[field.name] || ""}
              onChange={handleChange}
              focusClass={config.colors.focus}
            />
          ))}

        {/* Photos */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-bold text-slate-700">
            Photos / Evidence
          </label>

          <p className="text-[11px] text-slate-400">Add photos if available</p>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFiles}
            className="hidden"
          />

          <div className="flex flex-wrap gap-3 mt-2">
            {images.map((file, index) => (
              <div
                key={`${file.name}-${index}`}
                className="w-24 h-24 rounded-xl overflow-hidden border border-slate-100 shadow-sm relative"
              >
                <img
                  src={URL.createObjectURL(file)}
                  alt="Evidence"
                  className="w-full h-full object-cover"
                />

                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="absolute top-1 right-1 p-1 rounded-full bg-white/90 text-red-500 shadow"
                >
                  <FiTrash2 className="text-xs" />
                </button>
              </div>
            ))}

            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className={`w-24 h-24 border border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center gap-1 text-slate-400 hover:text-slate-600 hover:border-slate-400 transition-colors bg-slate-50/50`}
            >
              <FiPlus className="text-lg" />

              <span className="text-[11px] font-medium">Add more</span>
            </button>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>

          <button
            type="submit"
            className={`flex-1 py-2.5 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-colors shadow-sm ${config.colors.button}`}
          >
            <FiSave className="text-base" />
            Save Incident
          </button>
        </div>
      </form>
    </div>
  );
}

